import { TranslationKeys } from "src/@types/i18next";
import {
  ComponentAdditionalVehicleIcon,
  ComponentAirConditioningIcon,
  ComponentBrakesIcon,
  ComponentCoolingSystemIcon,
  ComponentDriveAxleIcon,
  ComponentElectricalIcon,
  ComponentEngineIcon,
  ComponentEnhancedElectricalIcon,
  ComponentExtraValueIcon,
  ComponentFuelSystemIcon,
  ComponentSealsAndGasketsIcon,
  ComponentSteeringIcon,
  ComponentSuspensionIcon,
  ComponentTransferCaseIcon,
  ComponentTransmissionIcon,
  ComponentTurbochargerIcon,
  ComponentHybridElectricIcon,
} from "@talladega/icons";
import React from "react";
import {
  EW_ADDITIONAL_VEHICLE_COMPONENTS,
  EW_HYBRID_ELECTRIC,
} from "@common/constants";
import {
  ExtendedWarrantyAmaAutoCanadaDigital,
  ExtendedWarrantyEssentialHMAutoCanadaDigital,
  ExtendedWarrantyEssentialVAAutoCanadaDigital,
  ExtendedWarrantyPremiumAutoCanadaDigital,
} from "./IAProgram";
import { superiorCoverage } from "./ExtendedWarrantyCoverageType";
import { IACoverageType } from "./IACoverageType";

export type WarrantyComponent = {
  id: string;
  name: TranslationKeys["purchase"] | TranslationKeys["common"];
  coverages: string[];
  details: {
    i18nkey: TranslationKeys["purchase"];
    coverageSpecificComponents?: {
      coverageType: IACoverageType;
      i18nkey: TranslationKeys["purchase"];
    };
  };
  icon?: JSX.Element;
};

type WarrantyComponentsByProgram = {
  [programId: string]: WarrantyComponent[];
};

const iconClassName = "block text-gray-700 m-auto w-6 h-6";

const additionalVehicleIcon = React.createElement(
  ComponentAdditionalVehicleIcon,
  {
    className: iconClassName,
  }
);

const airConditioningIcon = React.createElement(ComponentAirConditioningIcon, {
  className: iconClassName,
});

const brakesIcon = React.createElement(ComponentBrakesIcon, {
  className: iconClassName,
});

const coolingSystemIcon = React.createElement(ComponentCoolingSystemIcon, {
  className: iconClassName,
});

const driveAxleIcon = React.createElement(ComponentDriveAxleIcon, {
  className: iconClassName,
});

const electricalIcon = React.createElement(ComponentElectricalIcon, {
  className: iconClassName,
});

const engineIcon = React.createElement(ComponentEngineIcon, {
  className: iconClassName,
});

const enhancedElectricalIcon = React.createElement(
  ComponentEnhancedElectricalIcon,
  {
    className: iconClassName,
  }
);

const extraValueIcon = React.createElement(ComponentExtraValueIcon, {
  className: iconClassName,
});

const fuelSystemIcon = React.createElement(ComponentFuelSystemIcon, {
  className: iconClassName,
});

const sealsAndGasketsIcon = React.createElement(ComponentSealsAndGasketsIcon, {
  className: iconClassName,
});

const steeringIcon = React.createElement(ComponentSteeringIcon, {
  className: iconClassName,
});

const suspensionIcon = React.createElement(ComponentSuspensionIcon, {
  className: iconClassName,
});

const transferCaseIcon = React.createElement(ComponentTransferCaseIcon, {
  className: iconClassName,
});

const transmissionIcon = React.createElement(ComponentTransmissionIcon, {
  className: iconClassName,
});

const turbochargerIcon = React.createElement(ComponentTurbochargerIcon, {
  className: iconClassName,
});

const hybridElectricIcon = React.createElement(ComponentHybridElectricIcon, {
  className: iconClassName,
});

const warrantyComponentsByProgram: WarrantyComponentsByProgram = {
  [ExtendedWarrantyPremiumAutoCanadaDigital.id]: [
    {
      id: "engine",
      name: "common:engine",
      coverages: ["BA", "PR", "CL", "SU", "SW"],
      details: {
        i18nkey: "purchase:premium_engine_components",
        coverageSpecificComponents: {
          coverageType: superiorCoverage,
          i18nkey: "purchase:premium_engine_superior_only",
        },
      },
      icon: engineIcon,
    },
    {
      id: "turbocharger",
      name: "purchase:turbocharger",
      coverages: ["BA", "PR", "CL", "SU", "SW"],
      details: {
        i18nkey: "purchase:premium_turbocharger_components",
      },
      icon: turbochargerIcon,
    },
    {
      id: "transmission",
      name: "common:transmission",
      coverages: ["BA", "PR", "CL", "SU", "SW"],
      details: {
        i18nkey: "purchase:premium_transmission_components",
        coverageSpecificComponents: {
          coverageType: superiorCoverage,
          i18nkey: "purchase:premium_transmission_superior_only",
        },
      },
      icon: transmissionIcon,
    },
    {
      id: "transferCase",
      name: "purchase:transfer_case",
      coverages: ["BA", "PR", "CL", "SU", "SW"],
      details: {
        i18nkey: "purchase:premium_transfer_case_components",
        coverageSpecificComponents: {
          coverageType: superiorCoverage,
          i18nkey: "purchase:premium_transfer_case_superior_only",
        },
      },
      icon: transferCaseIcon,
    },
    {
      id: "driveAxle",
      name: "purchase:drive_axle",
      coverages: ["BA", "PR", "CL", "SU", "SW"],
      details: {
        i18nkey: "purchase:premium_drive_axle_components",
      },
      icon: driveAxleIcon,
    },
    {
      id: "steering",
      name: "purchase:steering",
      coverages: ["PR", "CL", "SU", "SW"],
      details: {
        i18nkey: "purchase:premium_steering_components",
      },
      icon: steeringIcon,
    },
    {
      id: "brakes",
      name: "purchase:brakes",
      coverages: ["PR", "CL", "SU", "SW"],
      details: {
        i18nkey: "purchase:premium_brakes_components",
        coverageSpecificComponents: {
          coverageType: superiorCoverage,
          i18nkey: "purchase:premium_brakes_superior_only",
        },
      },
      icon: brakesIcon,
    },
    {
      id: "airConditioner",
      name: "purchase:air_conditioner",
      coverages: ["PR", "CL", "SU", "SW"],
      details: {
        i18nkey: "purchase:premium_ac_components",
      },
      icon: airConditioningIcon,
    },
    {
      id: "fuelSystem",
      name: "purchase:fuel_system",
      coverages: ["PR", "CL", "SU", "SW"],
      details: {
        i18nkey: "purchase:premium_fuel_system_components",
      },
      icon: fuelSystemIcon,
    },
    {
      id: "electrical",
      name: "purchase:electrical",
      coverages: ["PR", "CL", "SU", "SW"],
      details: {
        i18nkey: "purchase:premium_electrical_components",
        coverageSpecificComponents: {
          coverageType: superiorCoverage,
          i18nkey: "purchase:premium_electrical_superior_only",
        },
      },
      icon: electricalIcon,
    },
    {
      id: "enhancedElectrical",
      name: "purchase:enhanced_electrical",
      coverages: ["CL", "SU", "SW"],
      details: {
        i18nkey: "purchase:premium_enhanced_electrical_components",
      },
      icon: enhancedElectricalIcon,
    },
    {
      id: "frontRearSuspensions",
      name: "purchase:front_rear_suspensions",
      coverages: ["CL", "SU", "SW"],
      details: {
        i18nkey: "purchase:premium_front_rear_suspension_components",
      },
      icon: suspensionIcon,
    },
    {
      id: "coolingSystem",
      name: "purchase:cooling_system",
      coverages: ["CL", "SU", "SW"],
      details: {
        i18nkey: "purchase:premium_cooling_components",
      },
      icon: coolingSystemIcon,
    },
    {
      id: EW_ADDITIONAL_VEHICLE_COMPONENTS,
      name: "purchase:additional_vehicle_components",
      coverages: ["SU", "SW"],
      details: {
        i18nkey: "purchase:premium_additional_vehicle_components",
      },
      icon: additionalVehicleIcon,
    },
  ],
  [ExtendedWarrantyEssentialVAAutoCanadaDigital.id]: [
    {
      id: "engine",
      name: "common:engine",
      coverages: ["LI", "P3", "P5"],
      details: {
        i18nkey: "purchase:essential_engine_components",
      },
      icon: engineIcon,
    },
    {
      id: "turbocharger",
      name: "purchase:turbocharger",
      coverages: ["LI", "P3", "P5"],
      details: {
        i18nkey: "purchase:essential_turbocharger_components",
      },
      icon: turbochargerIcon,
    },
    {
      id: "transmission",
      name: "common:transmission",
      coverages: ["LI", "P3", "P5"],
      details: {
        i18nkey: "purchase:essential_transmission_components",
      },
      icon: transmissionIcon,
    },
    {
      id: "transferCase",
      name: "purchase:transfer_case",
      coverages: ["LI", "P3", "P5"],
      details: {
        i18nkey: "purchase:essential_transfer_case_components",
      },
      icon: transferCaseIcon,
    },
    {
      id: "driveAxle",
      name: "purchase:drive_axle",
      coverages: ["LI", "P3", "P5"],
      details: {
        i18nkey: "purchase:essential_drive_axle_components",
      },
      icon: additionalVehicleIcon,
    },
    {
      id: "steering",
      name: "purchase:steering",
      coverages: ["LI", "P3", "P5"],
      details: {
        i18nkey: "purchase:essential_steering_components",
      },
      icon: steeringIcon,
    },
    {
      id: "brakes",
      name: "purchase:brakes",
      coverages: ["LI", "P3", "P5"],
      details: {
        i18nkey: "purchase:essential_brakes_components",
      },
      icon: brakesIcon,
    },
    {
      id: "electrical",
      name: "purchase:electrical",
      coverages: ["LI", "P3", "P5"],
      details: {
        i18nkey: "purchase:essential_electrical_components",
      },
      icon: electricalIcon,
    },
    {
      id: "fuelSystem",
      name: "purchase:fuel_system",
      coverages: ["LI", "P3", "P5"],
      details: {
        i18nkey: "purchase:essential_fuel_system_components",
      },
      icon: fuelSystemIcon,
    },
    {
      id: "frontSuspension",
      name: "purchase:front_suspension",
      coverages: ["LI", "P3", "P5"],
      details: {
        i18nkey: "purchase:essential_front_suspension_components",
      },
      icon: suspensionIcon,
    },
    {
      id: "sealsAndGaskets",
      name: "purchase:seals_and_gaskets",
      coverages: ["LI", "P3", "P5"],
      details: {
        i18nkey: "purchase:essential_seals_and_gaskets_components",
      },
      icon: sealsAndGasketsIcon,
    },
    {
      id: "airConditioner",
      name: "purchase:air_conditioner",
      coverages: ["LI", "P3", "P5"],
      details: {
        i18nkey: "purchase:essential_air_conditioner_components",
      },
      icon: airConditioningIcon,
    },
    {
      id: "extraValueProtection",
      name: "purchase:extra_value_protection",
      coverages: ["LI", "P3", "P5"],
      details: {
        i18nkey: "purchase:essential_extra_value_protection_components",
      },
      icon: extraValueIcon,
    },
    {
      id: EW_HYBRID_ELECTRIC,
      name: "purchase:hybrid_electric",
      coverages: ["LI", "P3", "P5"],
      details: {
        i18nkey: "purchase:hybrid_electric_components",
      },
      icon: hybridElectricIcon,
    },
  ],
  [ExtendedWarrantyEssentialHMAutoCanadaDigital.id]: [
    {
      id: "engine",
      name: "common:engine",
      coverages: ["LI", "P3", "P5"],
      details: {
        i18nkey: "purchase:essential_engine_components",
      },
      icon: engineIcon,
    },
    {
      id: "turbocharger",
      name: "purchase:turbocharger",
      coverages: ["LI", "P3", "P5"],
      details: {
        i18nkey: "purchase:essential_turbocharger_components",
      },
      icon: turbochargerIcon,
    },
    {
      id: "transmission",
      name: "common:transmission",
      coverages: ["LI", "P3", "P5"],
      details: {
        i18nkey: "purchase:essential_transmission_components",
      },
      icon: transmissionIcon,
    },
    {
      id: "transferCase",
      name: "purchase:transfer_case",
      coverages: ["LI", "P3", "P5"],
      details: {
        i18nkey: "purchase:essential_transfer_case_components",
      },
      icon: transferCaseIcon,
    },
    {
      id: "driveAxle",
      name: "purchase:drive_axle",
      coverages: ["LI", "P3", "P5"],
      details: {
        i18nkey: "purchase:essential_drive_axle_components",
      },
      icon: driveAxleIcon,
    },
    {
      id: "steering",
      name: "purchase:steering",
      coverages: ["LI", "P3", "P5"],
      details: {
        i18nkey: "purchase:essential_steering_components",
      },
      icon: steeringIcon,
    },
    {
      id: "brakes",
      name: "purchase:brakes",
      coverages: ["LI", "P3", "P5"],
      details: {
        i18nkey: "purchase:essential_brakes_components",
      },
      icon: brakesIcon,
    },
    {
      id: "electrical",
      name: "purchase:electrical",
      coverages: ["LI", "P3", "P5"],
      details: {
        i18nkey: "purchase:essential_electrical_components",
      },
      icon: electricalIcon,
    },
    {
      id: "fuelSystem",
      name: "purchase:fuel_system",
      coverages: ["LI", "P3", "P5"],
      details: {
        i18nkey: "purchase:essential_fuel_system_components",
      },
      icon: fuelSystemIcon,
    },
    {
      id: "frontSuspension",
      name: "purchase:front_suspension",
      coverages: ["LI", "P3", "P5"],
      details: {
        i18nkey: "purchase:essential_front_suspension_components",
      },
      icon: suspensionIcon,
    },
    {
      id: "sealsAndGaskets",
      name: "purchase:seals_and_gaskets",
      coverages: ["LI", "P3", "P5"],
      details: {
        i18nkey: "purchase:essential_seals_and_gaskets_components",
      },
      icon: sealsAndGasketsIcon,
    },
    {
      id: "airConditioner",
      name: "purchase:air_conditioner",
      coverages: ["LI", "P3", "P5"],
      details: {
        i18nkey: "purchase:essential_air_conditioner_components",
      },
      icon: airConditioningIcon,
    },
    {
      id: "extraValueProtection",
      name: "purchase:extra_value_protection",
      coverages: ["LI", "P3", "P5"],
      details: {
        i18nkey: "purchase:essential_extra_value_protection_components",
      },
      icon: extraValueIcon,
    },
    {
      id: EW_HYBRID_ELECTRIC,
      name: "purchase:hybrid_electric",
      coverages: ["LI", "P3", "P5"],
      details: {
        i18nkey: "purchase:hybrid_electric_components",
      },
      icon: hybridElectricIcon,
    },
  ],
  [ExtendedWarrantyAmaAutoCanadaDigital.id]: [
    {
      id: "engine",
      name: "common:engine",
      coverages: ["AM", "L2", "AA", "AB", "MA", "BB", "CC"],
      details: {
        i18nkey: "purchase:ama_engine_components",
      },
      icon: engineIcon,
    },
    {
      id: "turbocharger",
      name: "purchase:turbocharger",
      coverages: ["AM", "L2", "AA", "AB", "MA", "BB", "CC"],
      details: {
        i18nkey: "purchase:ama_turbocharger_components",
      },
      icon: turbochargerIcon,
    },
    {
      id: "transmission",
      name: "common:transmission",
      coverages: ["AM", "L2", "AA", "AB", "MA", "BB", "CC"],
      details: {
        i18nkey: "purchase:ama_transmission_components",
      },
      icon: transmissionIcon,
    },
    {
      id: "transferCase",
      name: "purchase:transfer_case",
      coverages: ["AM", "L2", "AA", "AB", "MA", "BB", "CC"],
      details: {
        i18nkey: "purchase:ama_transfer_case_components",
      },
      icon: transferCaseIcon,
    },
    {
      id: "driveAxle",
      name: "purchase:drive_axle",
      coverages: ["AM", "L2", "AA", "AB", "MA", "BB", "CC"],
      details: {
        i18nkey: "purchase:ama_drive_axle_components",
      },
      icon: driveAxleIcon,
    },
    {
      id: "steering",
      name: "purchase:steering",
      coverages: ["AM", "L2", "AA", "AB", "MA", "BB", "CC"],
      details: {
        i18nkey: "purchase:ama_steering_components",
      },
      icon: steeringIcon,
    },
    {
      id: "brakes",
      name: "purchase:brakes",
      coverages: ["AM", "L2", "AA", "AB", "MA", "BB", "CC"],
      details: {
        i18nkey: "purchase:ama_brakes_components",
      },
      icon: brakesIcon,
    },
    {
      id: "electrical",
      name: "purchase:electrical",
      coverages: ["AM", "L2", "AA", "AB", "MA", "BB", "CC"],
      details: {
        i18nkey: "purchase:ama_electrical_components",
      },
      icon: electricalIcon,
    },
    {
      id: "airConditioner",
      name: "purchase:air_conditioner",
      coverages: ["AM", "L2", "AA", "AB", "MA", "BB", "CC"],
      details: {
        i18nkey: "purchase:ama_ac_components",
      },
      icon: airConditioningIcon,
    },
    {
      id: "fuelSystem",
      name: "purchase:fuel_system",
      coverages: ["AM", "L2", "AA", "AB", "MA", "BB", "CC"],
      details: {
        i18nkey: "purchase:ama_fuel_system_components",
      },
      icon: fuelSystemIcon,
    },
    {
      id: "frontRearSuspensions",
      name: "purchase:front_rear_suspensions",
      coverages: ["AM", "L2", "AA", "AB", "MA", "BB", "CC"],
      details: {
        i18nkey: "purchase:ama_front_rear_suspension_components",
      },
      icon: suspensionIcon,
    },
    {
      id: "coolingSystem",
      name: "purchase:cooling_system",
      coverages: ["AM", "L2", "AA", "AB", "MA", "BB", "CC"],
      details: {
        i18nkey: "purchase:ama_cooling_components",
      },
      icon: coolingSystemIcon,
    },
    {
      id: "enhancedElectrical",
      name: "purchase:enhanced_electrical",
      coverages: ["AM", "L2", "AA", "AB", "MA", "BB", "CC"],
      details: {
        i18nkey: "purchase:ama_enhanced_electrical_components",
      },
      icon: enhancedElectricalIcon,
    },
    {
      id: EW_HYBRID_ELECTRIC,
      name: "purchase:hybrid_electric",
      coverages: ["AM", "L2", "AA", "AB", "MA", "BB", "CC"],
      details: {
        i18nkey: "purchase:hybrid_electric_components",
      },
      icon: hybridElectricIcon,
    },
  ],
};

export default warrantyComponentsByProgram;
